import {Injectable}               from '@angular/core';
import {OperationModesEnum}       from '../../stores/common/operation-modes.store';
import {ApplicationSettingsStore} from '../../stores/agronomic/application-settings.store';
import {CountryStore}             from '../../stores/common/country.store';
import {LoginStore}               from '../../stores/login/login.store';
import IApplicationSettings = Data.Agronomic.IApplicationSettings;

@Injectable({providedIn: 'root'})
export class ApplicationSettingsService {
  constructor(private loginStore: LoginStore,
              private countryStore: CountryStore,
              private applicationSettingsStore: ApplicationSettingsStore) {
  }

  /**
   * Checks if an EC value falls within the valid range of application settings
   * @param ecValue The EC (growth stage) value to validate
   * @param applicationSettings The application settings containing EC range
   * @returns Boolean indicating whether the EC value is valid for the given application settings
   * @public
   */
  public isApplicationSettingValidForGrowthStage(ecValue: number | undefined,
                                                 applicationSettings: IApplicationSettings | undefined): boolean {
    if (!applicationSettings || !Number.isFinite(ecValue)) {
      return false;
    }
    return applicationSettings.EcMin <= ecValue && ecValue <= applicationSettings.EcMax;
  }

  /**
   * Retrieves application settings for a specific operation mode and crop (EPPO code)
   * @param operationModeKey The operation mode to filter application settings
   * @param eppoCode The crop's EPPO code to match against application settings
   * @returns Matching application settings or undefined if no match is found
   * @public
   */
  public getApplicationSetting(operationModeKey: OperationModesEnum | OperationModesEnum[],
                               eppoCode: string): IApplicationSettings | undefined {
    const countryCode = this.loginStore.SelectedFarm.Country.Id;
    return this.getApplicationSettingsByCountryCode(countryCode)
      .filter(x => x.OperationMode.Key === operationModeKey)
      .find(x => x.EppoGroup.EppoCodes.some(y => y === eppoCode));
  }

  /**
   * Retrieves application settings based on the provided operation mode key(s), EPPO code,
   * and optional growth stages values filters.
   * @param operationModeKey - The operation mode key or an array of keys to filter the application settings.
   * @param eppoCode - The EPPO code used to further filter the application settings.
   * @param growthStages - An optional array of EC values used to filter the application settings within a specific EC range.
   * @return A list of application settings matching the provided filters. An empty array is returned if no matching settings are found.
   * @public
   */
  public getApplicationSettings(operationModeKey: OperationModesEnum | OperationModesEnum[],
                                eppoCode: string, growthStages?: number[]): IApplicationSettings[] {
    const operationModeKeys: OperationModesEnum[] = Array.isArray(operationModeKey) ? operationModeKey : [operationModeKey];
    const countryCode = this.loginStore.SelectedFarm.Country.Id;
    const applicationSettingsByEppoCode = this.getApplicationSettingsByCountryCode(countryCode)
      .filter(x => operationModeKeys.includes(x.OperationMode.Key as OperationModesEnum))
      .filter(x => x.EppoGroup.EppoCodes.some(y => y === eppoCode));
    if (applicationSettingsByEppoCode.length <= 0) {
      return [];
    }
    if (growthStages === undefined || growthStages.length <= 0) {
      return applicationSettingsByEppoCode;
    }
    return applicationSettingsByEppoCode.filter(x => growthStages.some(y => y >= x.EcMin && y <= x.EcMax));
  }

  /**
   * Retrieves application settings based on the provided country code.
   * @param countryCode - The country code used to filter application settings.
   * @return A list of application settings associated with the specified country code.
   * @private
   */
  private getApplicationSettingsByCountryCode(countryCode: string): IApplicationSettings[] {
    const countryGroups = this.countryStore.CountryGroupAssignments
      .filter(x => x.Country.Id === countryCode)
      .map(x => x.CountryGroup.Id);
    return this.applicationSettingsStore.Applications
      .filter(x => x.CountryGroups.some(y => countryGroups.includes(y)))
      .filter((item, index, self) => self.findIndex(x => x.Id === item.Id) === index);
  }
}
